import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Line, Bar } from 'react-chartjs-2';
import { useAuth } from '../../context/AuthContext';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHeartbeat,
  faTemperatureHigh,
  faTint,
  faDownload,
  faCircle
} from '@fortawesome/free-solid-svg-icons';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const SignosVitales = () => {
  const { user } = useAuth();
  const [datos, setDatos] = useState([]);

  useEffect(() => {
    if (!user) return;

    const fetchData = async () => {
      try {
        const res = await axios.get(`/api/users/signos/usuario/${user.id}`);
        setDatos(res.data.reverse());
      } catch (err) {
        console.error('Error al obtener signos vitales:', err);
      }
    };

    fetchData();
    const intervalId = setInterval(fetchData, 5000);
    return () => clearInterval(intervalId);
  }, [user]);

  const labels = datos.map(d => new Date(d.timestamp).toLocaleTimeString('es-MX'));

  const ritmoCardiacoData = {
    labels,
    datasets: [{
      label: 'Ritmo Cardiaco (ppm)',
      data: datos.map(d => d.ritmo_cardiaco),
      borderColor: 'rgb(255, 99, 132)',
      backgroundColor: 'rgba(255, 99, 132, 0.5)',
      tension: 0.3,
      fill: false,
    }]
  };

  const temperaturaCorporalData = {
    labels,
    datasets: [{
      label: 'Temperatura Corporal (°C)',
      data: datos.map(d => d.temp_corporal),
      backgroundColor: 'rgba(54, 162, 235, 0.7)',
      borderColor: 'rgb(54, 162, 235)',
      borderWidth: 1
    }]
  };

  const oxigenoSangreData = {
    labels,
    datasets: [{
      label: 'Oxígeno en Sangre (%)',
      data: datos.map(d => d.spo2),
      backgroundColor: 'rgba(75, 192, 192, 0.7)',
      borderColor: 'rgb(75, 192, 192)',
      borderWidth: 1
    }]
  };

  const commonOptions = {
    responsive: true,
    plugins: {
      legend: { position: 'top' },
      tooltip: { mode: 'index', intersect: false }
    },
    scales: {
      y: {
        beginAtZero: true
      }
    }
  };

  const Filtros = () => (
  <div className="flex justify-end gap-2 mb-2">
    <button
      className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
      onClick={generarPDF}
    >
      <FontAwesomeIcon icon={faDownload} /> PDF
    </button>
  </div>
);


  const generarPDF = async () => {
    const input = document.getElementById('reporte-signos'); // contenedor que quieres convertir a PDF
    const canvas = await html2canvas(input, { scale: 2 });
    const imgData = canvas.toDataURL('image/png');

    const pdf = new jsPDF('p', 'mm', 'a4');
    const imgProps = pdf.getImageProperties(imgData);
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
    pdf.save(`reporte_signos_${user.id}.pdf`);
  };

  return (
  <div className="w-full p-6 space-y-8">
  <h1 className="text-3xl font-bold text-gray-800 mb-6">Signos Vitales</h1>

  {/* Ritmo Cardiaco */}
  <div className="w-full backdrop-blur-md bg-white/30 shadow-md rounded-xl p-6 flex flex-col lg:flex-row lg:items-center gap-6">
    <div className="lg:w-1/2 text-black space-y-3">
      <h2 className="text-xl font-bold flex items-center gap-2 text-red-600">
        <FontAwesomeIcon icon={faHeartbeat} /> Ritmo Cardíaco
      </h2>
      <p>
        El ritmo cardíaco mide las pulsaciones por minuto (ppm) y refleja la
        eficiencia con la que el corazón bombea la sangre al resto del cuerpo.
        Los valores normales cambian según la edad:
      </p>
      <ul className="space-y-1">
        <li><b>Recién nacidos:</b> 100–160 ppm</li>
        <li><b>Niños (1–10 años):</b> 70–120 ppm</li>
        <li><b>Adultos:</b> 60–100 ppm</li>
        <li><b>Adultos mayores:</b> 60–100 ppm, pero pueden tener variaciones debido a medicamentos o enfermedades cardíacas.</li>
      </ul>
      <p>
        Mantener un ritmo cardíaco saludable es fundamental para asegurar un
        buen transporte de oxígeno y nutrientes. Un ritmo anormal persistente
        puede ser indicio de problemas como arritmias, insuficiencia cardíaca o
        estrés excesivo.
      </p>
      <ul className="space-y-1">
        <li className="flex items-center gap-2">
          <FontAwesomeIcon icon={faCircle} className="text-green-500" />
          Normal: 60–100 ppm
        </li>
        <li className="flex items-center gap-2">
          <FontAwesomeIcon icon={faCircle} className="text-orange-400" />
          Ligeramente elevado: 101–120 ppm
        </li>
        <li className="flex items-center gap-2">
          <FontAwesomeIcon icon={faCircle} className="text-red-500" />
          Taquicardia: &gt; 120 ppm
        </li>
        <li className="flex items-center gap-2">
          <FontAwesomeIcon icon={faCircle} className="text-blue-500" />
          Bradicardia: &lt; 60 ppm
        </li>
      </ul>
    </div>
    {/* Gráfica centrada verticalmente y sin estirarse */}
    <div className="lg:w-1/2 flex justify-center">
      <div id="reporte-signos" className="bg-white/80 p-4 rounded-xl shadow-md max-w-md w-full self-center">
        <Filtros />
        <Line data={ritmoCardiacoData} options={commonOptions} />
      </div>
    </div>
  </div>

  {/* Temperatura Corporal */}
  <div className="w-full backdrop-blur-md bg-white/30 shadow-md rounded-xl p-6 flex flex-col lg:flex-row lg:items-center gap-6">
    <div className="lg:w-1/2 text-black space-y-3">
      <h2 className="text-xl font-bold flex items-center gap-2 text-blue-500">
        <FontAwesomeIcon icon={faTemperatureHigh} /> Temperatura Corporal
      </h2>
      <p>
        La temperatura corporal refleja el equilibrio entre la producción y la
        pérdida de calor en el organismo. Los valores pueden variar levemente
        según la edad y la hora del día:
      </p>
      <ul>
        <li><b>Niños:</b> 36.4°C – 37.5°C</li>
        <li><b>Adultos:</b> 36.1°C – 37.2°C</li>
        <li><b>Adultos mayores:</b> 35.8°C – 36.8°C</li>
      </ul>
      <p>
        Una temperatura anormal puede indicar infecciones, inflamaciones o
        alteraciones metabólicas. Es clave para detectar fiebre en casos de
        infección o hipotermia en climas fríos.
      </p>
      <ul className="space-y-1">
        <li className="flex items-center gap-2">
          <FontAwesomeIcon icon={faCircle} className="text-green-500" />
          Normal: 36.1°C – 37.2°C
        </li>
        <li className="flex items-center gap-2">
          <FontAwesomeIcon icon={faCircle} className="text-orange-400" />
          Febrícula: 37.3°C – 38°C
        </li>
        <li className="flex items-center gap-2">
          <FontAwesomeIcon icon={faCircle} className="text-red-500" />
          Fiebre alta: &gt; 38°C
        </li>
        <li className="flex items-center gap-2">
          <FontAwesomeIcon icon={faCircle} className="text-blue-500" />
          Hipotermia: &lt; 35°C
        </li>
      </ul>
    </div>
     <div className="lg:w-1/2 flex justify-center">
      <div id="reporte-signos" className="bg-white/80 p-4 rounded-xl shadow-md max-w-md w-full self-center">
        <Filtros />
        <Bar data={temperaturaCorporalData} options={commonOptions} />
      </div>
    </div>
  </div>

  {/* Oxígeno en Sangre */}
  <div className="w-full backdrop-blur-md bg-white/30 shadow-md rounded-xl p-6 flex flex-col lg:flex-row lg:items-center gap-6">
    <div className="lg:w-1/2 text-black space-y-3">
      <h2 className="text-xl font-bold flex items-center gap-2 text-teal-700">
        <FontAwesomeIcon icon={faTint} /> Oxígeno en Sangre (SpO₂)
      </h2>
      <p>
        El nivel de oxígeno en sangre indica la eficiencia del intercambio de
        gases en los pulmones y el transporte de oxígeno a los tejidos. Los
        valores normales dependen del estado de salud y edad:
      </p>
      <ul>
        <li><b>Niños y adultos saludables:</b> 95% – 100%</li>
        <li><b>Adultos mayores:</b> 94% – 98% puede ser aceptable dependiendo de condiciones médicas</li>
        <li><b>Personas con EPOC:</b> 88% – 92% bajo supervisión médica</li>
      </ul>
      <p>
        Un nivel bajo de saturación puede ser signo de problemas respiratorios,
        cardíacos o de circulación. El monitoreo es crucial en pacientes con
        enfermedades pulmonares o durante tratamientos de oxigenoterapia.
      </p>
      <ul className="space-y-1">
        <li className="flex items-center gap-2">
          <FontAwesomeIcon icon={faCircle} className="text-green-500" />
          Normal: 95% – 100%
        </li>
        <li className="flex items-center gap-2">
          <FontAwesomeIcon icon={faCircle} className="text-orange-400" />
          Ligeramente bajo: 91% – 94%
        </li>
        <li className="flex items-center gap-2">
          <FontAwesomeIcon icon={faCircle} className="text-red-500" />
          Hipoxemia: &lt; 90%
        </li>
      </ul>
    </div>
    <div className="lg:w-1/2 flex justify-center">
      <div id="reporte-signos" className="bg-white/80 p-4 rounded-xl shadow-md max-w-md w-full self-center">
        <Filtros />
        <Bar data={oxigenoSangreData} options={commonOptions} />
      </div>
    </div>
  </div>
</div>

);

};

export default SignosVitales;
